import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { differenceInDays, format, isAfter, isThisYear } from 'date-fns';
import { Button, CardView, rem, Separator } from 'design-system';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import LogoBankAccount from 'design-system/LogoBankAccount';
import { Animated, Easing, View } from 'react-native';
import Text from 'design-system/Text';
import Status from 'design-system/Status';
import BlurAmountView from 'design-system/BlurAmountView';
import { formatDateForItem, formatPlural, formatSortCodeAndAccountNumber, } from 'utils';
import { useForceSyncSingle } from 'hooks/useForceSync';
import IconRepeat from 'design-system/icons/IconRepeat';
import useColors from 'hooks/useColors';
import { useOpenConnectionLogin } from 'hooks/useOpenConnectionLogin';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { canRenewConnection, isBankAccountBroken, } from 'features/settings/logins/utils';
import { selectConnectionById } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../../constants';
export const AccountMenuCard = ({ account, ModalToUse }) => {
    const styles = useStyles(styleSet);
    const [lastSyncAt, setLastSyncAt] = useState(account.lastSyncAt);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const connection = useAppSelector((store) => selectConnectionById(store, account.bankConnectionId));
    const savingsPlanAccountId = useAppSelector((state) => state.pots.savingsPlan?.bankInfo?.accountId);
    const isAccountLinkedToAutosave = savingsPlanAccountId === account.id;
    const rows = useMemo(() => {
        const arr = [];
        if (account.isManual && account.createdAt) {
            arr.push({
                title: 'Created',
                value: formatDateForItem(account.createdAt),
            });
        }
        if (!account.isManual && lastSyncAt) {
            arr.push({
                title: 'Last synced',
                value: format(lastSyncAt, isThisYear(lastSyncAt) ? 'd MMM h:mma' : 'd MMM yyyy h:mma'),
            });
        }
        if (connection?.consentExpiresAt) {
            const days = differenceInDays(connection.consentExpiresAt, new Date());
            arr.push({
                title: 'Bank connection',
                value: isAfter(new Date(), connection.consentExpiresAt)
                    ? 'Expired'
                    : `${days} ${formatPlural('day', 'days', days)} left`,
            });
        }
        if (isAccountLinkedToAutosave) {
            arr.push({
                title: 'Autosave',
                value: <Status activeText="Linked" inactiveText="Linked" active/>,
            });
        }
        return arr;
    }, [
        account.createdAt,
        account.isManual,
        connection?.consentExpiresAt,
        isAccountLinkedToAutosave,
        lastSyncAt,
    ]);
    const details = useMemo(() => {
        const { type, sortCode, accountNumber } = account;
        if (!sortCode && !accountNumber)
            return null;
        let accNumber = accountNumber;
        if (accountNumber?.length === 4) {
            if (type === 'CREDITCARD') {
                accNumber = `•••• •••• •••• ${account.accountNumber}`;
            }
            else {
                accNumber = `•••••• ${account.accountNumber}`;
            }
        }
        return (<BlurAmountView enabled={scrambled}>
        <Text TextThin-14 numberOfLines={1} Secondary>
          {formatSortCodeAndAccountNumber(sortCode, accNumber)}
        </Text>
      </BlurAmountView>);
    }, [account, scrambled]);
    const [isSyncing, sync] = useForceSyncSingle();
    const [spinValue] = useState(new Animated.Value(0));
    const onPressSync = useCallback(async () => {
        const res = await sync(account.id, ModalToUse.hide);
        if (res)
            setLastSyncAt(new Date().toISOString());
    }, [ModalToUse, account.id, sync]);
    const spinAnim = useRef(Animated.timing(spinValue, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
        isInteraction: false,
    }));
    const spin = useCallback(() => {
        spinValue.setValue(0);
        spinAnim.current.start(({ finished }) => {
            if (isSyncing && finished) {
                spin();
            }
        });
    }, [isSyncing, spinValue]);
    useEffect(() => {
        if (isSyncing) {
            spin();
        }
        else {
            spinValue.setValue(0);
            spinAnim.current.reset();
        }
    }, [isSyncing, spin, spinValue]);
    const colors = useColors();
    const leftIcon = useMemo(() => (<Animated.View style={[
            {
                transform: [
                    {
                        rotate: spinValue.interpolate({
                            inputRange: [0, 1],
                            outputRange: ['0deg', '360deg'],
                        }),
                    },
                ],
            },
        ]}>
        <IconRepeat width={16} height={16} color={colors.text.textOnBrand}/>
      </Animated.View>), [colors.text.textOnBrand, spinValue]);
    const needsToRenewAccess = isBankAccountBroken(account);
    const showSeparator = (!account.isManual && !account.isClosed) || rows.length > 0;
    const navigation = useMainStackNavigation();
    const login = useOpenConnectionLogin(navigation);
    const onOpenLogins = useCallback((data) => login({ type: 'account', data }), [login]);
    const onRenewAccess = useCallback(() => ModalToUse.hide(() => onOpenLogins(account)), [ModalToUse, account, onOpenLogins]);
    const { width } = useAppFrameDimensions();
    const remainingBtnSpace = useMemo(() => width - rem(64) - GAP - MIN_RENEW_BUTTON_WIDTH, [width]);
    const showRenewButton = !!connection && canRenewConnection(connection);
    return (<CardView noMarginTop style={styles.card}>
      <View style={styles.name}>
        <LogoBankAccount size="R44" data={account}/>
        <View style={styles.flex}>
          <Text Text-16 numberOfLines={1}>
            {account.name}
          </Text>
          {details}
        </View>
      </View>
      {showSeparator ? <Separator style={styles.separator}/> : null}
      <View style={styles.content}>
        {rows.map((row) => (<View key={row.title} style={styles.row}>
            <Text TextThin-14 Secondary>
              {row.title}
            </Text>
            {typeof row.value === 'string' ? (<Text Text-14>{row.value}</Text>) : (row.value)}
          </View>))}

        {!account.isManual &&
            !account.isClosed &&
            (needsToRenewAccess ? (<Button square brandReversed title="Renew access" onPress={onRenewAccess}/>) : (<View style={[styles.buttonContainer]}>
              <Button square brandReversed title="Sync" onPress={onPressSync} leftIconContainerStyle={styles.syncIcon} leftIcon={leftIcon} containerStyle={[styles.syncButton]}/>
              {showRenewButton ? (<Button square brandReversed title="Renew connection" onPress={onRenewAccess} containerStyle={isWeb || remainingBtnSpace <= MIN_RENEW_BUTTON_WIDTH
                        ? styles.renewButtonXS
                        : styles.renewButton}/>) : null}
            </View>))}
      </View>
    </CardView>);
};
const GAP = rem(10);
const MIN_RENEW_BUTTON_WIDTH = rem(153);
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    card: {
        backgroundColor: colors.background.dark,
        marginBottom: rem(8),
        ...(isWeb && {
            marginHorizontal: 0,
        }),
    },
    name: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
        padding: rem(16),
    },
    separator: {
        backgroundColor: colors.borders.divider,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: rem(16),
    },
    syncIcon: {
        paddingRight: rem(8),
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: GAP,
    },
    syncButton: {
        flex: 1,
    },
    renewButton: {
        flex: 1,
        paddingHorizontal: rem(10),
    },
    renewButtonXS: {
        flex: 0,
        minWidth: MIN_RENEW_BUTTON_WIDTH,
        paddingHorizontal: rem(10),
    },
    content: {
        padding: rem(16),
        gap: rem(16),
    },
}));
